/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ChartLineIcon } from '@rsmus/myrsm-icons'
import { tokens } from 'src/styles/materialTheme'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import { Styles } from '../../types'

interface ConfirmRemoveChildReportDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  childReportDisplayName?: string
  parentReportName?: string
}

const styles: Styles = {
  iconCell: {
    display: 'flex',
    justifyContent: 'center',
    border: 0,
    paddingTop: '1.5rem',
  },
  title: {
    fontFamily: 'Prelo, sans-serif',
    color: tokens.colors.rsmGray.copy,
    fontWeight: 300,
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    letterSpacing: 0,
    textAlign: 'center',
    border: 0,
    padding: '0.5rem',
  },
  subtitle: {
    fontFamily: 'Prelo, sans-serif',
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: 0,
    color: 'text.secondary',
    textAlign: 'center',
    padding: '0.5rem',
    border: 0,
  },
  names: {
    fontFamily: 'Prelo, sans-serif',
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: 0,
    textAlign: 'center',
    padding: '0.5rem',
    border: 0,
  },
  actionsCell: {
    border: 0,
    paddingTop: '2rem',
    paddingBottom: '1rem',
  },
  actions: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.625rem',
  },
  iconWrap: {
    width: '7.5rem',
    height: '7.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '7.5rem',
      height: '7.5rem',
    },
  },
}

const ConfirmRemoveChildReportDialog = ({
  open,
  onClose,
  onConfirm,
  childReportDisplayName,
  parentReportName,
}: ConfirmRemoveChildReportDialogProps) => {
  const { t } = useTranslation()
  const desktopMaxWidth = '41rem'
  const desktopMaxHeight = '30rem'

  return (
    <Box className="Rsm-ProfileModal">
      <ResponsiveModal
        open={open}
        onClose={onClose}
        aria-label={t('PowerBI.RemoveChildReportModal.Title')}
        desktopMaxHeight={desktopMaxHeight}
        desktopMaxWidth={desktopMaxWidth}
        removeHeaderSpacing
        data-testid="Lbl_PowerBI_RemoveChildReportDialog_Modal">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={styles.iconCell}>
                <Box sx={styles.iconWrap}>
                  <ChartLineIcon />
                </Box>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell
                sx={styles.title}
                data-testid="Tbl_PowerBI_RemoveChildReportDialog_Title">
                {t('PowerBI.RemoveChildReportModal.Header')}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell
                sx={styles.subtitle}
                data-testid="Tbl_PowerBI_RemoveChildReportDialog_Subtitle">
                {t('PowerBI.RemoveChildReportModal.SubHeader')}
              </TableCell>
            </TableRow>

            {(childReportDisplayName || parentReportName) && (
              <TableRow>
                <TableCell
                  sx={styles.names}
                  data-testid="Tbl_PowerBI_RemoveChildReportDialog_Names">
                  {childReportDisplayName} – {parentReportName}
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell sx={styles.actionsCell}>
                <Stack direction="row" sx={styles.actions}>
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    data-testid="Btn_PowerBI_RemoveChildReportDialog_Cancel">
                    {t('PowerBI.RemoveChildReportModal.Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={onConfirm}
                    data-testid="Btn_PowerBI_RemoveChildReportDialog_Confirm">
                    {t('PowerBI.RemoveChildReportModal.Confirm')}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </ResponsiveModal>
    </Box>
  )
}

export default ConfirmRemoveChildReportDialog
